// useWbcStwClaimOrchestrator.ts

import { useCallback, useEffect, useRef } from "react";
import { WorldAuthClient } from "@bridge-applications/world-auth";
import {
    ApiWbcStwClaimCompleteReqSchema,
    ApiWbcStwClaimCompleteResSchema,
    ApiWbcStwClaimPendingReqSchema,
    ApiWbcStwClaimPendingResSchema,
} from "@bridge-applications/eh-types";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../../state/config";
import { claim } from "../../claim";
import EhUniversalVoucher from "../../abi/EhUniversalVoucher.json";

export const WBC_STW_CLAIM_POLL_KEY =
    "__earnHub__wbc_stw_claim_poll_v1";

type WbcStwClaimPollIntent = {
    type: "WBC_STW";
    campaignId: string;
    internalCampaignId: string;
    startedAt: number;
    pollUntil: number;
};

function readPollIntent(): WbcStwClaimPollIntent | null {
    if (typeof window === "undefined") return null;

    try {
        const raw = localStorage.getItem(WBC_STW_CLAIM_POLL_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as Partial<WbcStwClaimPollIntent>;
        if (
            parsed.type !== "WBC_STW" ||
            typeof parsed.campaignId !== "string" ||
            typeof parsed.internalCampaignId !== "string" ||
            typeof parsed.startedAt !== "number" ||
            typeof parsed.pollUntil !== "number"
        ) {
            localStorage.removeItem(WBC_STW_CLAIM_POLL_KEY);
            return null;
        }

        if (parsed.pollUntil <= Date.now()) {
            localStorage.removeItem(WBC_STW_CLAIM_POLL_KEY);
            return null;
        }

        return parsed as WbcStwClaimPollIntent;
    } catch {
        try {
            localStorage.removeItem(WBC_STW_CLAIM_POLL_KEY);
        } catch {
            /* noop */
        }
        return null;
    }
}

function clearPollIntent(): void {
    if (typeof window === "undefined") return;
    try {
        localStorage.removeItem(WBC_STW_CLAIM_POLL_KEY);
    } catch {
        /* noop */
    }
}

function isVisible(): boolean {
    return (
        typeof document === "undefined" || document.visibilityState === "visible"
    );
}

function getNextPollDelayMs(intent: WbcStwClaimPollIntent): number {
    const elapsed = Date.now() - intent.startedAt;
    if (elapsed < 2 * 60 * 1000) return 2 * 1000;
    if (elapsed < 5 * 60 * 1000) return 10 * 1000;
    return 30 * 1000;
}

function getTerminalErrorKey(message: string): string | null {
    if (message === "CLAIM_EXPIRED") return "WbcStwClaim.expired";
    if (message === "WALLET_MISMATCH") return "WbcStwClaim.walletMismatch";
    if (message === "SPIN_MISMATCH") return "WbcStwClaim.error";
    if (message === "MINIKIT_CLAIM_FAILED") return "WbcStwClaim.error";
    if (message === "COMPLETE_FAILED") return "WbcStwClaim.error";
    return null;
}

type UseWbcStwClaimOrchestratorOptions = {
    appSessionLoading: boolean | undefined;
};

export function useWbcStwClaimOrchestrator({
    appSessionLoading,
}: UseWbcStwClaimOrchestratorOptions): void {
    const { t } = useTranslation();
    const inFlightRef = useRef(false);
    const timerRef = useRef<number | null>(null);
    const processPendingClaimRef = useRef<() => void>(() => undefined);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const scheduleNextPoll = useCallback(
        (intent: WbcStwClaimPollIntent) => {
            clearTimer();
            if (!isVisible()) return;

            const remaining = intent.pollUntil - Date.now();
            if (remaining <= 0) {
                clearPollIntent();
                return;
            }

            const delay = Math.min(getNextPollDelayMs(intent), remaining);
            timerRef.current = window.setTimeout(() => {
                processPendingClaimRef.current();
            }, delay);
        },
        [clearTimer],
    );

    const processPendingClaim = useCallback(() => {
        const intent = readPollIntent();
        if (!intent) {
            clearTimer();
            return;
        }

        if (!isVisible()) return;
        if (appSessionLoading !== false) {
            scheduleNextPoll(intent);
            return;
        }
        if (inFlightRef.current) return;

        inFlightRef.current = true;

        const run = async () => {
            let toastId: string | undefined;

            try {
                const accessToken =
                    await WorldAuthClient.getAccessTokenSilently();
                if (!accessToken) {
                    throw new Error("UNAUTHENTICATED");
                }

                const pendingRes = await fetch(
                    `${API_BASE}/api/v1/wbc/stw-claim/pending`,
                    {
                        method: "POST",
                        headers: {
                            "content-type": "application/json",
                            authorization: `Bearer ${accessToken.accessToken}`,
                        },
                        body: JSON.stringify(
                            ApiWbcStwClaimPendingReqSchema.parse({
                                internal_campaign_id:
                                    intent.internalCampaignId,
                            }),
                        ),
                    },
                );

                const pendingJson = await pendingRes.json().catch(() => null);
                const pending =
                    ApiWbcStwClaimPendingResSchema.safeParse(pendingJson);

                if (!pending.success) {
                    throw new Error("PENDING_FAILED");
                }
                if (pending.data.status === "ERROR") {
                    throw new Error(pending.data.code ?? "PENDING_FAILED");
                }
                if (!pendingRes.ok) {
                    throw new Error("PENDING_FAILED");
                }

                if (pending.data.status === "NONE") {
                    scheduleNextPoll(intent);
                    return;
                }

                if (pending.data.status === "EXPIRED") {
                    clearPollIntent();
                    toast.error(
                        t("WbcStwClaim.expired", {
                            defaultValue: "Could not claim reward. Please try again.",
                        }),
                    );
                    return;
                }

                if (pending.data.status === "FULFILLED") {
                    clearPollIntent();
                    toast.success(
                        t("WbcStwClaim.alreadyClaimed", {
                            defaultValue: "Reward already claimed.",
                        }),
                    );
                    return;
                }

                toastId = toast.loading(
                    t("WbcStwClaim.resolving", {
                        defaultValue: "Preparing your reward…",
                    }),
                );

                const { voucher } = pending.data;
                const verifyingContract = voucher.domain.verifyingContract;

                toast.loading(
                    t("WbcStwClaim.claiming", {
                        defaultValue: "Claiming reward…",
                    }),
                    { id: toastId },
                );

                const txRes = await claim([
                    {
                        address: verifyingContract,
                        abi: EhUniversalVoucher.abi,
                        functionName: "claim",
                        args: [voucher.struct, voucher.signature],
                    },
                ]);

                if ((txRes as { status?: string })?.status === "error") {
                    throw new Error("MINIKIT_CLAIM_FAILED");
                }

                const transactionId = (txRes as { transaction_id?: string })
                    ?.transaction_id;

                const completeRes = await fetch(
                    `${API_BASE}/api/v1/wbc/stw-claim/complete`,
                    {
                        method: "POST",
                        headers: {
                            "content-type": "application/json",
                            authorization: `Bearer ${accessToken.accessToken}`,
                        },
                        body: JSON.stringify(
                            ApiWbcStwClaimCompleteReqSchema.parse({
                                internal_campaign_id:
                                    intent.internalCampaignId,
                                spin_id: pending.data.spin_id,
                                transaction_id: transactionId,
                            }),
                        ),
                    },
                );

                const completeJson = await completeRes.json().catch(() => null);
                const completed =
                    ApiWbcStwClaimCompleteResSchema.safeParse(completeJson);

                if (!completed.success) {
                    throw new Error("COMPLETE_FAILED");
                }
                if (completed.data.status === "ERROR") {
                    throw new Error(completed.data.code ?? "COMPLETE_FAILED");
                }
                if (!completeRes.ok) {
                    throw new Error("COMPLETE_FAILED");
                }

                clearPollIntent();

                const alreadyFulfilled =
                    completed.data.state.status === "ALREADY_FULFILLED";

                toast.success(
                    alreadyFulfilled
                        ? t("WbcStwClaim.alreadyClaimed", {
                              defaultValue: "Reward already claimed.",
                          })
                        : t("WbcStwClaim.success", {
                              defaultValue: "Reward claimed!",
                          }),
                    { id: toastId },
                );
            } catch (err: unknown) {
                const message =
                    err instanceof Error ? err.message : String(err);
                const terminalErrorKey = getTerminalErrorKey(message);

                if (terminalErrorKey) {
                    clearPollIntent();
                } else {
                    scheduleNextPoll(intent);
                }

                if (toastId || terminalErrorKey) {
                    toast.error(
                        t(
                            message === "UNAUTHENTICATED"
                                ? "WbcStwClaim.signInRequired"
                                : (terminalErrorKey ?? "WbcStwClaim.error"),
                            {
                                defaultValue:
                                    message === "UNAUTHENTICATED"
                                        ? "Sign in to claim your reward."
                                        : "Could not claim reward. Please try again.",
                            },
                        ),
                        toastId ? { id: toastId } : undefined,
                    );
                }

            } finally {
                inFlightRef.current = false;
            }
        };

        void run();
    }, [appSessionLoading, clearTimer, scheduleNextPoll, t]);

    processPendingClaimRef.current = processPendingClaim;

    useEffect(() => {
        processPendingClaim();
    }, [processPendingClaim]);

    useEffect(() => {
        const onResume = () => {
            processPendingClaim();
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                processPendingClaim();
            }
        };

        window.addEventListener("pageshow", onResume);
        window.addEventListener("focus", onResume);
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            window.removeEventListener("pageshow", onResume);
            window.removeEventListener("focus", onResume);
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
        };
    }, [processPendingClaim]);

    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, [clearTimer]);
}
